import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import LanguageSelector from './components/LanguageSelector';
import LoginForm from './components/LoginForm';
import SecurityBadges from './components/SecurityBadges';
import CredentialsInfo from './components/CredentialsInfo';

const Login = () => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'fr';
  });

  const [direction, setDirection] = useState(() => {
    return localStorage.getItem('direction') || 'ltr';
  });

  useEffect(() => {
    document.documentElement?.setAttribute('dir', direction);
  }, [direction]);

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    const newDirection = newLanguage === 'ar' ? 'rtl' : 'ltr';
    setDirection(newDirection);
    localStorage.setItem('language', newLanguage);
    localStorage.setItem('direction', newDirection);
  };

  const translations = {
    fr: {
      title: 'Connexion - LegalOfficeManager',
      heading: 'Bienvenue',
      subheading: 'Connectez-vous à votre compte pour gérer votre cabinet juridique',
      logoAlt: 'Logo LegalOfficeManager avec balance de justice dorée sur fond bleu marine professionnel',
      heroImageAlt: 'Bureau d\'avocat marocain moderne avec bibliothèque juridique, bureau en bois et décoration professionnelle'
    },
    ar: {
      title: 'تسجيل الدخول - LegalOfficeManager',
      heading: 'مرحباً بك',
      subheading: 'قم بتسجيل الدخول إلى حسابك لإدارة مكتب المحاماة الخاص بك',
      logoAlt: 'شعار LegalOfficeManager مع ميزان العدالة الذهبي على خلفية زرقاء داكنة احترافية',
      heroImageAlt: 'مكتب محاماة مغربي حديث مع مكتبة قانونية ومكتب خشبي وديكور احترافي'
    }
  };

  const t = translations?.[language];

  return (
    <>
      <Helmet>
        <title>{t?.title}</title>
        <meta name="description" content="Secure login portal for LegalOfficeManager - Moroccan law firm management system" />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6 lg:p-8" style={{ direction }}>
        <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
          {/* Left Column - Hero Section */}
          <div className="hidden lg:flex flex-col justify-center space-y-6 md:space-y-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-elevation-md">
                <Icon name="Scale" size={32} color="#FFFFFF" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
                  LegalOfficeManager
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  {language === 'fr' ? 'Gestion de cabinet juridique' : 'إدارة مكتب المحاماة'}
                </p>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-elevation-lg">
              <Image
                src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800"
                alt={t?.heroImageAlt}
                className="w-full h-[400px] md:h-[500px] object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                  {language === 'fr' ? 'Gérez votre cabinet avec efficacité' : 'إدارة مكتبك بكفاءة'}
                </h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  {language === 'fr' ? 'Solution complète pour la gestion des clients, dossiers et facturation' : 'حل شامل لإدارة العملاء والقضايا والفواتير'}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Login Form */}
          <div className="w-full max-w-md mx-auto lg:max-w-none">
            <div className="bg-card border border-border rounded-2xl shadow-elevation-lg p-6 md:p-8 lg:p-10">
              {/* Mobile Logo */}
              <div className="lg:hidden flex items-center justify-center gap-3 mb-6 md:mb-8">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Icon name="Scale" size={24} color="#FFFFFF" />
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground">Avocat Manager

                </h1>
              </div>

              <div className="text-center mb-6 md:mb-8">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2 md:mb-3">
                  {t?.heading}
                </h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t?.subheading}
                </p>
              </div>

              <LanguageSelector
                currentLanguage={language}
                onLanguageChange={handleLanguageChange} />


              <LoginForm language={language} direction={direction} />

              <SecurityBadges language={language} />

              <CredentialsInfo language={language} />
            </div>

            {/* Footer */}
            <div className="mt-6 md:mt-8 text-center">
              <p className="text-xs md:text-sm text-muted-foreground">
                {language === 'fr' ?
                `© ${new Date()?.getFullYear()} LegalOfficeManager. Tous droits réservés.` :
                `© ${new Date()?.getFullYear()} LegalOfficeManager. جميع الحقوق محفوظة.`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default Login;